// src/components/ui/ServiceCard.tsx
import React from 'react';
import { Star, Clock, CheckCircle, Sparkles, Heart, Award } from 'lucide-react';

interface ServiceCardProps {
  service: {
    id: string;
    name: string;
    category: 'nails' | 'eyelashes' | 'makeup' | 'packages' | 'addons';
    price: number;
    duration: string;
    description: string;
    popular: boolean;
    features: string[];
    ghanaianNote?: string;
    includes?: string[];
    bestFor?: string[];
  };
}

// Local function to format prices in Ghana Cedis
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

// Category color mapping
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'nails':
      return 'bg-pink-100 text-pink-700 border-pink-200';
    case 'eyelashes':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'makeup':
      return 'bg-rose-100 text-rose-700 border-rose-200';
    case 'packages':
      return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    case 'addons':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    default:
      return 'bg-mauve-100 text-mauve-700 border-mauve-200';
  }
};

const ServiceCard: React.FC<ServiceCardProps> = ({ service }) => {
  const categoryColor = getCategoryColor(service.category);

  return (
    <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-mauve-100 hover:border-mauve-300 hover:transform hover:-translate-y-1 h-full flex flex-col">
      {/* Header section */}
      <div className="p-6 border-b border-mauve-100">
        {/* Category and popular badges */}
        <div className="flex justify-between items-center mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${categoryColor}`}>
            {service.category === 'nails' && ' Nails'}
            {service.category === 'eyelashes' && ' Eyelashes'}
            {service.category === 'makeup' && ' Makeup'}
            {service.category === 'packages' && ' Package'}
            {service.category === 'addons' && 'Add-on'}
          </span>
          
          {service.popular && (
            <div className="flex items-center space-x-1 bg-gradient-to-r from-mauve-500 to-mauve-600 text-white px-3 py-1 rounded-full shadow-sm">
              <Star size={14} className="fill-yellow-300" />
              <span className="text-xs font-medium">Popular</span>
            </div>
          )}
        </div>
        
        {/* Service name */}
        <h3 className="text-xl font-bold text-mauve-900 group-hover:text-mauve-700 transition-colors mb-3">
          {service.name}
        </h3>
        
        {/* Description */}
        <p className="text-mauve-600 mb-4 text-sm">{service.description}</p>
        
        {/* Ghanaian note */}
        {service.ghanaianNote && (
          <div className="flex items-start mb-4 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200">
            <span className="text-yellow-600 mr-2 mt-0.5">🇬🇭</span>
            <span className="text-xs text-yellow-800 font-medium">{service.ghanaianNote}</span>
          </div>
        )}
        
        {/* Price and duration */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex flex-col">
            <div className="text-2xl font-bold text-mauve-800 bg-gradient-to-br from-mauve-50 to-white px-4 py-2 rounded-lg border border-mauve-200">
              {formatPrice(service.price)}
              {service.category === 'packages' && (
                <span className="text-xs font-normal text-green-600 ml-2">
                  (Save up to 20%)
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center text-mauve-600 bg-mauve-50 px-3 py-1 rounded-lg">
            <Clock size={16} className="mr-2 text-mauve-500" />
            <span className="font-medium text-sm">{service.duration}</span>
          </div>
        </div>
      </div>
      
      {/* Main content section */}
      <div className="p-6 flex-grow flex flex-col">
        {/* Features */}
        <div className="mb-6">
          <h4 className="font-medium text-mauve-800 mb-3 flex items-center">
            <CheckCircle size={18} className="text-mauve-500 mr-2" />
            Service Includes:
          </h4>
          <ul className="space-y-2">
            {service.features.map((feature, index) => (
              <li key={index} className="flex items-start text-mauve-700 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-mauve-400 mt-1.5 mr-3 flex-shrink-0"></div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Best for section */}
        {service.bestFor && service.bestFor.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-mauve-800 mb-3 flex items-center">
              <Sparkles size={18} className="text-mauve-500 mr-2" />
              Perfect For:
            </h4>
            <div className="flex flex-wrap gap-2">
              {service.bestFor.map((item, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-mauve-50 text-mauve-700 text-xs rounded-full border border-mauve-200"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Includes section */}
        {service.includes && service.includes.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-mauve-800 mb-3 flex items-center">
              <Award size={18} className="text-mauve-500 mr-2" />
              Also Includes:
            </h4>
            <ul className="space-y-1">
              {service.includes.map((item, index) => (
                <li key={index} className="flex items-start text-mauve-700 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 mr-3 flex-shrink-0"></div>
                  <span className="text-green-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* Action buttons */}
      <div className="mt-auto p-6 pt-4 border-t border-mauve-100">
        <div className="flex gap-3">
          <a 
            href={`https://wa.me/233594197765?text=Hello GetNailedbyAJ! I'm interested in booking: ${service.name} - ${formatPrice(service.price)} (${service.duration})`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 btn-primary py-3 text-center hover:shadow-lg transition-shadow"
          >
            Book Now
          </a>
          <button className="btn-outline px-4 py-3 hover:bg-mauve-50 transition-colors">
            <Heart size={18} className="text-mauve-500" />
          </button>
        </div>
        <div className="text-center mt-3">
          <span className="text-xs text-mauve-500">
             Westlands Blvd, Hatso, Accra •  0594197765
          </span>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;